'use client';

import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { cva } from 'class-variance-authority';
import { ComponentPropsWithoutRef, createContext, forwardRef, useContext, useMemo } from 'react';
import * as React from 'react';

import { cn } from '../utils/cn';
import { Icon, type IconDefinition } from '../Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'small' | 'default' | 'large' | 'huge';
export type ButtonTheme = 'light' | 'dark';

type BaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconDefinition;
  iconProps?: Omit<React.ComponentProps<typeof Icon>, 'icon'>;
  iconPosition?: 'left' | 'right';
  theme?: ButtonTheme;
  screenReaderLabel?: string;
  loading?: boolean;
  asChild?: boolean;
  disabled?: boolean;
};

export type BaseButtonProps = BaseProps &
  ComponentPropsWithoutRef<'button'> & { [dataAttibute: `data-${string}`]: string } & {
    href?: never | string;
  };
export type BaseAnchorProps = BaseProps & ComponentPropsWithoutRef<'a'> & { href: string };
export type ButtonProps = BaseAnchorProps | BaseButtonProps;

const buttonStyles = cva(
  'inline-flex shrink-0 select-none whitespace-nowrap items-center justify-center font-sans font-semibold focus-visible:outline focus-visible:outline-[1.5px] focus-visible:outline-offset-2 focus-visible:outline-brand-400 focus-visible:ring-0',
  {
    variants: {
      variant: {
        primary:
          'bg-gradient-to-b from-[#5648FB] to-[#3C2EDD] dark:from-[#5648FB] dark:to-[#3C2EDD] text-white shadow-[inset_0_0_0_1px] shadow-brand-800/30 dark:shadow-brand-400/50 [text-shadow:0_1px_1px_rgba(2,6,23,0.25)] ring-[3px] ring-brand-300 dark:ring-brand-400 ring-opacity-0 dark:ring-opacity-0 hover:ring-opacity-30 hover:shadow-brand-800/60 dark:hover:shadow-brand-300/50',
        secondary:
          'bg-white dark:bg-transparent text-slate-800 dark:text-slate-100 shadow-[inset_0_0_0_1px] shadow-slate-300 dark:shadow-white/30 ring-[3px] ring-slate-300 dark:ring-slate-600 ring-opacity-0 dark:ring-opacity-0 hover:ring-opacity-20 hover:shadow-slate-400 dark:hover:shadow-slate-300/50',
        ghost:
          'bg-slate-300/10 text-slate-800 dark:text-slate-100 hover:bg-slate-300/20 dark:hover:bg-slate-300/20',
        danger:
          'bg-red-600 text-white focus-visible:outline-red-400 shadow-[inset_0_0_0_1px] shadow-red-700/30 dark:shadow-red-300/50 [text-shadow:0_1px_1px_rgba(183,28,28,0.25)] ring-[3px] ring-red-500 dark:ring-red-500 ring-opacity-0 dark:ring-opacity-0 hover:ring-opacity-20 hover:shadow-red-800/60 dark:hover:ring-opacity-25 dark:hover:shadow-red-300/70',
        link: 'text-slate-800 dark:text-slate-100 underline decoration-slate-400 dark:decoration-slate-600 bg-transparent decoration-1 underline-offset-4 hover:decoration-slate-600 dark:hover:decoration-slate-400',
      },
      size: {
        small: 'rounded-md px-[0.625rem] py-1.5 text-[13px] leading-[15px] gap-1.5',
        default: 'rounded-md px-3 py-2 text-[13px] leading-4 gap-1.5',
        large: 'rounded-md px-4 py-2 text-base gap-2',
        huge: 'rounded-lg px-5 py-3 text-base gap-2.5',
      },
      iconOnly: {
        true: 'flex items-center justify-center',
        false: '',
      },
      disabled: {
        true: 'cursor-default opacity-50',
        false: 'cursor-pointer',
      },
      inGroup: {
        true: '-ml-[1px] first:ml-0 [&:not(:first-of-type)]:rounded-l-none [&:not(:last-of-type)]:rounded-r-none hover:z-10 focus-visible:z-20',
        false: '',
      },
    },
    compoundVariants: [
      {
        iconOnly: true,
        size: 'small',
        className: 'size-[27px]  rounded-md p-0',
      },
      {
        iconOnly: true,
        size: 'default',
        className: 'size-8 rounded-md',
      },
      {
        iconOnly: true,
        size: 'large',
        className: 'size-10  rounded-md',
      },
      {
        iconOnly: true,
        size: 'huge',
        className: 'size-12 rounded-lg',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      disabled: false,
      inGroup: false,
    },
  },
);

const iconStyles = cva('inline-flex shrink-0 items-center justify-center', {
  variants: {
    variant: {
      primary: 'text-brand-200',
      secondary: 'text-slate-500 dark:text-slate-100',
      ghost: 'text-slate-800 dark:text-slate-100',
      danger: 'text-red-200',
      link: 'text-slate-800 dark:text-slate-100',
    },
    size: {
      small: 'size-3.5',
      default: 'size-4',
      large: 'size-4',
      huge: 'size-5',
    },
    iconOnly: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'secondary',
      iconOnly: true,
      className: 'text-inherit dark:text-inherit',
    },
    {
      variant: 'ghost',
      iconOnly: true,
      className: 'text-inherit dark:text-inherit',
    },
  ],
  defaultVariants: {
    variant: 'primary',
    size: 'default',
  },
});

interface ButtonGroupContextValue {
  variant?: ButtonVariant;
  size?: ButtonSize;
  theme?: ButtonTheme;
}

const ButtonGroupContext = createContext<ButtonGroupContextValue | null>(null);

/**
 * A clickable element that triggers an action.
 * @see {@link https://stylus.design/button}
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant: variantProp = 'primary',
      size: sizeProp = 'default',
      icon,
      iconProps = {},
      iconPosition = 'left',
      theme: themeProp = 'light',
      screenReaderLabel,
      className,
      children,
      loading,
      asChild = false,
      disabled = false,
      href,
      ...props
    },
    ref,
  ) => {
    const groupContext = useContext(ButtonGroupContext);
    const { className: iconClassName, ...restIconProps } = iconProps;
    const iconOnly = icon !== undefined && children === undefined;
    const label = asChild ? (
      children
    ) : (
      <span
        className={cn({
          '!w-fit': children,
        })}
      >
        {children}
      </span>
    );

    // Use ButtonGroup context values if available, otherwise use Button props
    const variant = groupContext?.variant || variantProp;
    const size = groupContext?.size || sizeProp;
    const theme = groupContext?.theme || themeProp;

    const buttonClassName = cn(
      buttonStyles({
        variant,
        size,
        iconOnly,
        disabled,
        inGroup: groupContext !== null,
      }),
      className,
    );

    const contents = (
      <>
        {loading && (
          <span className='mr-2'>
            <Icon icon={faSpinnerThird} spin />
          </span>
        )}
        {icon ? (
          <>
            {children && iconPosition === 'right' && label}
            <Icon
              icon={icon}
              className={cn(iconStyles({ variant, size, iconOnly }), iconClassName)}
              {...restIconProps}
            />
            {screenReaderLabel && <span className='sr-only'>{screenReaderLabel}</span>}
            {children && iconPosition === 'left' && label}
          </>
        ) : (
          children
        )}
      </>
    );

    if (href) {
      return (
        <a
          {...(props as BaseAnchorProps)}
          className={buttonClassName}
          href={disabled ? undefined : href}
          role={disabled ? 'link' : undefined}
          aria-disabled={disabled}
          data-theme={theme}
          ref={ref as React.RefObject<HTMLAnchorElement>}
        >
          {contents}
        </a>
      );
    }

    return (
      <button
        {...(props as BaseButtonProps)}
        className={buttonClassName}
        disabled={disabled}
        data-theme={theme}
        ref={ref as React.RefObject<HTMLButtonElement>}
      >
        {contents}
      </button>
    );
  },
);

export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  theme?: ButtonTheme;
}

/**
 * Group multiple buttons together without space between.
 * @see {@link https://stylus.design/components/button}
 */
export const ButtonGroup = ({
  children,
  variant,
  theme,
  size,
  className,
  ...props
}: ButtonGroupProps) => {
  const contextValue = useMemo(() => ({ variant, size, theme }), [variant, size, theme]);

  return (
    <ButtonGroupContext.Provider value={contextValue}>
      <div role='group' className={cn('inline-flex', className)} {...props}>
        {children}
      </div>
    </ButtonGroupContext.Provider>
  );
};
